import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ArrowRight, Wind, Activity, Target, Moon, Briefcase, Brain, CheckCircle2, Linkedin, Instagram, Facebook, MapPin } from "lucide-react";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/152101b1-2824-4e75-80a7-f7a4ca1944a3/images/2ab49b75a691e32957bcb504e35006a59dc6b3fc084e39dfdce5c2ffa0560915.png";
const EXEC_IMG = "https://images.unsplash.com/photo-1755120219659-70ba8fe2a2c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxleGVjdXRpdmUlMjByZXN0aW5nJTIwY2FsbXxlbnwwfHx8fDE3Nzg5MTU4Njd8MA&ixlib=rb-4.1.0&q=85";

const BENEFITS = [
  { icon: Activity, title: "Reduce stress under pressure", body: "Drop into a calm-but-sharp state in under 5 minutes." },
  { icon: Target, title: "Sharper focus on demand", body: "Train coherent rhythms that improve sustained attention." },
  { icon: Brain, title: "Faster recovery", body: "Shift the nervous system into recovery between sessions and meetings." },
  { icon: Moon, title: "Deeper, restorative sleep", body: "Down-regulating breath patterns to drop you into delta-rich rest." },
  { icon: Briefcase, title: "Built for executives & teams", body: "Performance protocols used by founders, athletes, and operators." },
  { icon: Wind, title: "Nervous system control", body: "From reactive to deliberate — own your physiology under load." },
];

export default function Landing() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [latestArticles, setLatestArticles] = useState([]);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setLatestArticles((r.data || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  const captureLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { email, source: "landing_hero" });
      setDone(true);
      toast.success("You're on the list. Watch for next steps.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageMeta
        title="Train your breath. Own your performance."
        description="Nervous system control for performance. Structured breathwork for executives, athletes, and high-performing teams. Join the Academy or book a discovery call."
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain-bg pointer-events-none" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-10 pb-16 sm:pt-16 sm:pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 z-10 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="overline text-primary mb-6"
            >
              Nervous System Control For Performance
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-foreground max-w-3xl"
            >
              Train your breath. <br />Own your performance.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              Structured breathwork for business owners, corporate teams, and high-performing professionals.
              Reduce stress, sharpen focus, recover faster, and perform better under pressure.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3"
            >
              <Link to="/register" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto rounded-full px-8" data-testid="hero-get-started-btn">
                  Join the Academy <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/book?type=Discovery+Call" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8 border-primary text-primary hover:bg-secondary/40" data-testid="hero-discovery-btn">
                  Book a discovery call
                </Button>
              </Link>
            </motion.div>

            {/* Lead capture */}
            <motion.form
              onSubmit={captureLead}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <Input
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-full px-5 bg-card"
                data-testid="lead-capture-email-input"
              />
              <Button
                type="submit"
                disabled={submitting || done}
                size="lg"
                className="rounded-full"
                data-testid="lead-capture-submit-btn"
              >
                {done ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Added</> : submitting ? "Sending..." : "Notify me"}
              </Button>
            </motion.form>
            <p className="text-xs text-muted-foreground mt-3">Get one short, practical breathwork insight per week. No spam.</p>
          </div>

          {/* Floating visual */}
          <div className="lg:col-span-5 relative order-1 lg:order-2 max-w-xs mx-auto sm:max-w-sm lg:max-w-none w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2 }}
              className="relative aspect-square"
            >
              <div className="absolute inset-0 rounded-full bg-secondary/40 blur-3xl" />
              <img src={HERO_IMG} alt="Breathwork concentric circles" className="relative z-10 w-full h-full object-contain" />
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-[18%] rounded-full bg-secondary/60 mix-blend-multiply"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20 lg:py-28">
          <div className="max-w-2xl mb-10 sm:mb-16">
            <p className="overline text-primary mb-3">Why breathwork</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Performance is downstream of your nervous system.
            </h2>
            <p className="mt-4 sm:mt-5 text-muted-foreground leading-relaxed">
              You can't out-think a dysregulated state. Breath is the fastest, most direct lever to shift it.
              Our protocols are built around the demands of real work — deals, sets, deadlines, and high-stakes rooms.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, idx) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
              >
                <Card className="p-7 h-full hover:border-primary/40 transition-colors">
                  <div className="h-10 w-10 rounded-full bg-secondary/50 text-primary flex items-center justify-center mb-5">
                    <b.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-semibold text-lg tracking-tight mb-2">{b.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{b.body}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate split */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="rounded-2xl sm:rounded-3xl overflow-hidden aspect-[4/5] lg:aspect-square">
          <img src={EXEC_IMG} alt="Executive resting" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="overline text-primary mb-3">For Teams</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            Build a calmer, sharper, more resilient team.
          </h2>
          <p className="mt-4 sm:mt-5 text-muted-foreground leading-relaxed max-w-lg">
            We run breathwork workshops for leadership teams, sales floors, and high-pressure operating units.
            Bring measurable nervous system control into your culture.
          </p>
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/corporate" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto rounded-full" data-testid="cta-corporate-btn">
                Enquire about team workshops
              </Button>
            </Link>
            <Link to="/book?type=Discovery+Call" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-primary text-primary" data-testid="cta-discovery-btn">
                Book a discovery call
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest articles */}
      {latestArticles.length > 0 && (
        <section className="bg-card border-y border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20 lg:py-24">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-12">
              <div>
                <p className="overline text-primary mb-3">Insights</p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                  Latest articles
                </h2>
              </div>
              <Link to="/articles" className="inline-flex items-center text-sm font-medium text-primary hover:underline" data-testid="landing-articles-all-link">
                All articles <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {latestArticles.map((a) => (
                <Link key={a.id} to={`/articles/${a.slug}`} className="block group" data-testid={`landing-article-${a.slug}`}>
                  <Card className="overflow-hidden h-full hover:border-primary/40 transition-colors flex flex-col">
                    {a.cover_image_url ? (
                      <div className="aspect-[16/9] bg-muted overflow-hidden">
                        <img
                          src={resolveImageUrl(a.cover_image_url)}
                          alt={a.title}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                    <div className="p-7 flex flex-col flex-1">
                      <div className="text-xs text-muted-foreground mb-3">
                        {a.published_at ? new Date(a.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}
                      </div>
                      <h3 className="font-display text-lg font-semibold tracking-tight mb-2 group-hover:text-primary transition-colors">{a.title}</h3>
                      {a.excerpt && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">{a.excerpt}</p>}
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary mt-4">
                        Read <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA strip */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-20 lg:py-24 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl mx-auto">
            Ready to take control of your state?
          </h2>
          <p className="mt-4 sm:mt-5 text-primary-foreground/80 max-w-xl mx-auto">
            Join the Academy, pick a practice, and feel the shift in under five minutes.
          </p>
          <div className="mt-8 flex flex-col items-center gap-4">
            <Link to="/register" className="w-full sm:w-auto max-w-sm">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full px-10 bg-white text-primary hover:bg-white/90" data-testid="cta-bottom-register-btn">
                Join the Academy <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <a
                href="https://www.linkedin.com/in/evan-l-gill?utm_source=share_via&utm_content=profile&utm_medium=member_android"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect on LinkedIn"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                data-testid="cta-linkedin-btn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/evangill_elitebreathwork/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow on Instagram"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                data-testid="cta-instagram-btn"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.facebook.com/EvanGillBreathwork"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow on Facebook"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                data-testid="cta-facebook-btn"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 items-start">
          <div className="flex flex-col gap-3 items-start">
            <img src="https://customer-assets.emergentagent.com/job_breathwork-elite/artifacts/sv3jydix_EGBW%20Logo%20100%20x%2084mm%20%281%29.png" alt="Evan Gill — Elite Performance Through Breath" className="h-14 w-auto" />
            <p className="text-xs text-muted-foreground max-w-xs">Nervous system control for performance.</p>
          </div>

          <div>
            <p className="overline text-primary mb-3">Visit</p>
            <a
              href="https://maps.google.com/?q=Unit+4%2F50+Parker+Ct%2C+Pinkenba+QLD+4008"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-start gap-2 text-sm text-foreground hover:text-primary transition-colors"
              data-testid="footer-address-link"
            >
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <span>Unit 4/50 Parker Ct<br />Pinkenba QLD 4008</span>
            </a>
          </div>

          <div>
            <p className="overline text-primary mb-3">Connect</p>
            <div className="flex flex-col gap-2 text-sm">
              <a
                href="https://www.linkedin.com/in/evan-l-gill?utm_source=share_via&utm_content=profile&utm_medium=member_android"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                data-testid="footer-linkedin-link"
              >
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
              <a
                href="https://www.instagram.com/evangill_elitebreathwork/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                data-testid="footer-instagram-link"
              >
                <Instagram className="h-4 w-4" /> Instagram
              </a>
              <a
                href="https://www.facebook.com/EvanGillBreathwork"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                data-testid="footer-facebook-link"
              >
                <Facebook className="h-4 w-4" /> Facebook
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-muted-foreground">
            <span>&copy; {new Date().getFullYear()} Evan Gill — Elite Performance Through Breath</span>
            <a href="https://evangill.xyz" className="hover:text-primary transition-colors" data-testid="footer-back-to-main-site">← Back to evangill.xyz</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
